import React from 'react';
import { cn } from '../../../lib/utils';
import TailwindButton from '../../Tailwind/TailwindButton';
import { useAppStore } from '../../../shared/stores/useAppStore';
import { UTCOffset } from '../../HistoryModal';
import { ErpConnection } from '../types';
import { ERP_TYPES } from '../constants';

interface ListViewProps {
    connections: ErpConnection[];
    loading: boolean;
    isAdmin: boolean;
    onAddConnection: () => void;
    onViewDetails: (connection: ErpConnection) => void;
}

export default function ListView({
    connections,
    loading,
    isAdmin,
    onAddConnection,
    onViewDetails,
}: ListViewProps) {
    const userSettings = useAppStore(s => s.userSettings);
    const getCurrentTzDate = (dateStr: string) => {
        const utcDate = new Date(dateStr);
        return new Date(utcDate.getTime() + UTCOffset * 60000);
    };

    const formatDate = (date: Date) => {
        if (userSettings.dateSystem === 'DMY') {
            return date.toLocaleString('en-GB');
        }
        return date.toLocaleString('en-US');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                    <div className="size-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-slate-400">Loading connections...</p>
                </div>
            </div>
        );
    }

    if (connections.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="size-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                    <span className="material-symbols-outlined text-[32px] text-slate-300">cable</span>
                </div>
                <p className="text-sm font-medium text-slate-500">
                    No ERP connections found
                </p>
                <p className="text-xs text-slate-400 mt-1 mb-4">
                    Connect your ERP system to get started
                </p>
                {isAdmin && (
                    <TailwindButton
                        variant="primary"
                        icon="add"
                        onClick={onAddConnection}
                    >
                        Add your first connection
                    </TailwindButton>
                )}
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            {isAdmin && (
                <div className="flex justify-end mb-4">
                    <TailwindButton
                        variant="primary"
                        size="sm"
                        icon="add"
                        onClick={onAddConnection}
                    >
                        Add Connection
                    </TailwindButton>
                </div>
            )}

            <table className="w-full">
                <thead>
                    <tr className="border-b border-slate-100">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                            Created
                        </th>
                        {isAdmin && (
                            <th className="px-4 py-3 w-16" />
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {connections.map((conn) => (
                        <tr
                            key={conn.id}
                            className={cn(
                                "group transition-colors",
                                isAdmin ? "hover:bg-slate-50/50 cursor-pointer" : ""
                            )}
                            onClick={() => isAdmin && onViewDetails(conn)}
                        >
                            <td className="px-4 py-3.5">
                                <span className="text-sm font-medium text-slate-900">{conn.name}</span>
                            </td>
                            <td className="px-4 py-3.5">
                                <div className="flex items-center gap-2">
                                    {ERP_TYPES[conn.type]?.logo && (
                                        <img
                                            src={ERP_TYPES[conn.type].logo}
                                            alt={conn.type}
                                            className="h-5 object-contain"
                                        />
                                    )}
                                    <span className="text-sm text-slate-600">
                                        {ERP_TYPES[conn.type]?.label || conn.type}
                                    </span>
                                </div>
                            </td>
                            <td className="px-4 py-3.5 hidden sm:table-cell">
                                <span className="text-sm text-slate-400">
                                    {formatDate(getCurrentTzDate(conn.created_at))}
                                </span>
                            </td>
                            {isAdmin && (
                                <td className="px-4 py-3.5">
                                    <div className="flex justify-end">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onViewDetails(conn);
                                            }}
                                            className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center"
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-slate-400">
                                                edit
                                            </span>
                                        </button>
                                    </div>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
